import { useCallback, useEffect, useRef } from 'react';

/**
 * ResizeObserver on a wrapper div — the responsive contract for every
 * chart primitive (T5). Returns a ref to attach to the wrapper and calls
 * `resize()` (via the provided callback) whenever the wrapper box changes.
 * echarts-for-react owns instance creation/disposal; this hook never
 * touches the instance lifecycle, only its canvas size.
 */
export function useChartResize(resize) {
  const wrapperRef = useRef(null);
  const resizeRef = useRef(resize);
  resizeRef.current = resize;

  useEffect(() => {
    const element = wrapperRef.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      resizeRef.current?.();
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return wrapperRef;
}

/** Resolve the raw echarts instance from an echarts-for-react core ref. */
export function getInstanceFromRef(chartRef) {
  return chartRef.current?.getEchartsInstance?.() || null;
}

export function useResizeChartInstance(chartRef) {
  return useCallback(() => {
    const instance = getInstanceFromRef(chartRef);

    if (instance && !instance.isDisposed()) {
      instance.resize();
    }
  }, [chartRef]);
}
